import React, { createContext, useState, useEffect, useContext } from 'react';
import i18n from '../i18n';

// API Gateway URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mkn5m2nvd4.execute-api.us-east-1.amazonaws.com/dev';

// Default content values (will be replaced with API calls later)
const defaultContent = {
  site: {
    title: 'Dozer',
    title_zh: '道择工程',
    description: 'Professional Services',
    description_zh: '专业工程服务',
    logo: '/images/logo.svg',
    favicon: '/favicon.ico',
    phone: '',
    email: 'info@dozer.com',
    address: '123 Main Street, City, State 10001',
    address_zh: '中国市区示例街123号',
    social: {
      facebook: '',
      twitter: '',
      instagram: '',
      linkedin: 'https://www.linkedin.com/company/dozer-electrical-llc',
    }
  },
  header: {
    navigation: [
      { title: 'Home', title_zh: '首页', path: '/' },
      { title: 'About', title_zh: '关于我们', path: '/#about' },
      { title: 'Services', title_zh: '服务', path: '/#services' },
      { title: 'Products', title_zh: '产品', path: '/#products' },
      { title: 'Projects', title_zh: '工程案例', path: '/#projects' },
      { title: 'Contact', title_zh: '联系我们', path: '/#contact' },
    ],
    cta: {
      text: 'Get a Quote',
      text_zh: '获取报价',
      path: '/contact',
    }
  },
  home: {
    hero: {
      title: 'Driving the Grid Forward',
      title_zh: '推动电网前行',
      subtitle: 'Experience reliability and excellence in every project',
      subtitle_zh: '在每个项目中体验可靠性和卓越性',
      description: 'We are dedicated to driving the grid forward through innovative engineering and high-performance electrical solutions. We specialize in future-proofing energy infrastructure, ensuring that as the world evolves, your power stays ahead of the curve.',
      description_zh: '我们致力于通过创新工程和高性能电气解决方案推动电网前行。我们专注于面向未来的能源基础设施建设，确保随着世界的发展，您的电力始终保持领先。',
      image: '/images/hero-bg.jpg',
      cta: {
        primary: {
          text: 'Our Products',
          text_zh: '我们的服务',
          path: '/#services',
        },
        secondary: {
          text: 'Contact Us',
          text_zh: '联系我们',
          path: '/#contact',
        }
      }
    },
    features: [
      {
        title: 'Experienced Team',
        title_zh: '经验丰富的团队',
        description: 'Our expert team brings years of industry experience to your project.',
        description_zh: '我们的专家团队为您的项目带来多年的行业经验。',
        icon: 'people',
      },
      {
        title: 'Quality Guarantee',
        title_zh: '质量保证',
        description: 'We stand behind our work with a 100% quality guarantee.',
        description_zh: '我们的工作提供100%的质量保证。',
        icon: 'verified',
      },
      {
        title: 'Modern Equipment',
        title_zh: '现代化设备',
        description: 'Using the latest technology for efficient and precise results.',
        description_zh: '使用最新技术，确保高效精确的结果。',
        icon: 'construction',
      },
      {
        title: '24/7 Support',
        title_zh: '24/7全天候支持',
        description: 'Round-the-clock customer service for all your needs.',
        description_zh: '全天候客户服务，满足您的所有需求。',
        icon: 'support_agent',
      },
    ],
    about: {
      title: 'About Our Company',
      title_zh: '关于我们',
      subtitle: 'Professional Excellence Since 1995',
      subtitle_zh: '自1995年以来的专业服务',
      description: 'For over 25 years, we have been delivering exceptional service and innovative solutions to our clients. Our commitment to quality and customer satisfaction has made us a leader in the industry.',
      description_zh: '在过去的25年里，我们一直为客户提供卓越的服务和创新解决方案。我们对质量和客户满意度的承诺使我们成为行业的领导者。',
      statistics: [
        { value: '25+', label: 'Years of Experience', label_zh: '年经验' },
        { value: '500+', label: 'Projects Completed', label_zh: '已完成项目' },
        { value: '100+', label: 'Professional Staff', label_zh: '专业人员' },
        { value: '50+', label: 'Industry Awards', label_zh: '行业奖项' },
      ],
      image: '/images/about.jpg',
      cta: {
        text: 'Learn More',
        text_zh: '了解更多',
        path: '/#about',
      }
    },
    services: {
      title: 'Our Products',
      title_zh: '我们的产品',
      subtitle: 'What We Offer',
      subtitle_zh: '我们能为您做什么',
      items: [
        {
          title: 'Single-Phase Pad Mounted Transformer',
          title_zh: '单相箱式变压器',
          description: 'High-efficiency pad-mounted transformers for residential and light commercial applications.',
          description_zh: '适用于住宅和轻型商业应用的高效箱式变压器。',
          icon: 'transformer',
          path: '/#products',
          image: '/images/products/1.png',
          fullDescription: 'Our single-phase pad-mounted transformers are designed for underground distribution systems, offering reliable power delivery for residential neighborhoods and light commercial areas. Features include tamper-resistant enclosures, loop or radial feed capability, and various kVA ratings to meet your specific needs.',
          fullDescription_zh: '我们的单相箱式变压器专为地下配电系统设计，为住宅区和轻型商业区提供可靠的电力输送。特点包括防篡改外壳、环路或放射式供电能力，以及多种kVA额定值以满足您的特定需求。'
        },
        {
          title: 'Single-Phase Oil Transformer',
          title_zh: '单相油浸式变压器',
          description: 'Traditional oil-immersed transformers with excellent cooling and insulation properties.',
          description_zh: '具有优异冷却和绝缘性能的传统油浸式变压器。',
          icon: 'transformer',
          path: '/#products',
          image: '/images/products/2.png',
          fullDescription: 'Single-phase oil-immersed transformers provide reliable power distribution with superior cooling efficiency. Ideal for pole-mounted or platform installations, these transformers offer long service life and excellent overload capability for various distribution applications.',
          fullDescription_zh: '单相油浸式变压器提供可靠的电力分配，具有卓越的冷却效率。非常适合杆式或平台安装，这些变压器为各种配电应用提供长寿命和出色的过载能力。'
        },
        {
          title: 'Dry Type Transformer',
          title_zh: '干式变压器',
          description: 'Environmentally friendly transformers ideal for indoor installations and sensitive environments.',
          description_zh: '环保型变压器，非常适合室内安装和敏感环境。',
          icon: 'transformer',
          path: '/#products',
          image: '/images/products/3.png',
          fullDescription: 'Dry-type transformers use air as the cooling medium, eliminating the need for oil and making them perfect for indoor applications, hospitals, schools, and high-rise buildings. They offer fire-resistant properties and require minimal maintenance.',
          fullDescription_zh: '干式变压器使用空气作为冷却介质，无需使用油，使其非常适合室内应用、医院、学校和高层建筑。它们具有防火特性，维护要求低。'
        },
        {
          title: 'Three-Phase Pad Mounted Transformer',
          title_zh: '三相箱式变压器',
          description: 'Heavy-duty transformers for commercial and industrial underground distribution systems.',
          description_zh: '适用于商业和工业地下配电系统的重型变压器。',
          icon: 'transformer',
          path: '/#products',
          image: '/images/products/4.png',
          fullDescription: 'Three-phase pad-mounted transformers deliver high-capacity power for commercial complexes, industrial facilities, and large-scale residential developments. Available in various configurations including radial feed, loop feed, and dual voltage options.',
          fullDescription_zh: '三相箱式变压器为商业综合体、工业设施和大型住宅开发项目提供大容量电力。提供多种配置，包括放射式供电、环路供电和双电压选项。'
        },
        {
          title: 'Up to 550kV Power Transformer',
          title_zh: '550kV及以下电力变压器',
          description: 'Ultra-high voltage power transformers for transmission and substation applications.',
          description_zh: '用于输电和变电站应用的超高压电力变压器。',
          icon: 'transformer',
          path: '/#products',
          image: '/images/products/5.png',
          fullDescription: 'Our high-voltage power transformers up to 550kV are engineered for utility-scale transmission networks and major substations. These transformers feature advanced insulation systems, on-load tap changers, and comprehensive monitoring capabilities for reliable grid operation.',
          fullDescription_zh: '我们的550kV及以下高压电力变压器专为公用事业级输电网络和大型变电站设计。这些变压器具有先进的绝缘系统、有载分接开关和全面的监控功能，确保电网可靠运行。'
        },
        {
          title: 'Up to 800kV Dead Tank Circuit Breaker',
          title_zh: '800kV及以下罐式断路器',
          description: 'High-voltage circuit breakers with grounded tank design for maximum safety.',
          description_zh: '采用接地罐设计的高压断路器，确保最大安全性。',
          icon: 'breaker',
          path: '/#products',
          image: '/images/products/6.png',
          fullDescription: 'Dead tank circuit breakers up to 800kV provide reliable interruption of fault currents in high-voltage transmission systems. The grounded tank design offers enhanced safety and allows for easy integration of current transformers within the breaker tank.',
          fullDescription_zh: '800kV及以下罐式断路器在高压输电系统中提供可靠的故障电流切断。接地罐设计提供增强的安全性，并允许在断路器罐内轻松集成电流互感器。'
        },
        {
          title: 'Capacitor Voltage Transformer',
          title_zh: '电容式电压互感器',
          description: 'Precision voltage measurement devices for high-voltage transmission systems.',
          description_zh: '用于高压输电系统的精密电压测量设备。',
          icon: 'cvt',
          path: '/#products',
          image: '/images/products/7.png',
          fullDescription: 'Capacitor Voltage Transformers (CVTs) provide accurate voltage measurement and power line carrier communication coupling for transmission systems. They offer excellent transient response and long-term stability for metering and protection applications.',
          fullDescription_zh: '电容式电压互感器(CVT)为输电系统提供精确的电压测量和电力线载波通信耦合。它们为计量和保护应用提供优异的瞬态响应和长期稳定性。'
        },
        {
          title: 'Up to 800kV Disconnect Switchgear',
          title_zh: '800kV及以下隔离开关',
          description: 'High-voltage disconnect switches for safe isolation of electrical equipment.',
          description_zh: '用于安全隔离电气设备的高压隔离开关。',
          icon: 'switch',
          path: '/#products',
          image: '/images/products/8.png',
          fullDescription: 'Our disconnect switchgear up to 800kV provides visible isolation for maintenance and safety operations in substations and transmission systems. Available in various configurations including vertical break, horizontal break, and pantograph designs.',
          fullDescription_zh: '我们的800kV及以下隔离开关为变电站和输电系统的维护和安全操作提供可见隔离。提供多种配置，包括垂直断开、水平断开和受电弓设计。'
        },
      ],
      cta: {
        text: 'View All Services',
        text_zh: '查看所有服务',
        path: '/#services',
      }
    },
    testimonials: [
      {
        name: 'John Smith',
        title: 'Project Manager, ABC Construction',
        title_zh: '项目经理，ABC建设公司',
        text: 'Exceptional service and quality work. The team was professional and completed the project ahead of schedule.',
        text_zh: '卓越的服务和高质量的工作。团队专业，提前完成了项目。',
        rating: 5,
        image: '/images/testimonials/john.jpg',
      },
      {
        name: 'Sarah Johnson',
        title: 'Director, XYZ Development',
        title_zh: '主管，XYZ开发公司',
        text: 'We have been working with Dozer for over 5 years and they consistently deliver outstanding results.',
        text_zh: '我们与道择合作已超过5年，他们始终如一地提供出色的结果。',
        rating: 5,
        image: '/images/testimonials/sarah.jpg',
      },
      {
        name: 'Michael Brown',
        title: 'Owner, Brown Properties',
        title_zh: '业主，布朗地产',
        text: 'The attention to detail and customer service is unmatched. I highly recommend their services.',
        text_zh: '他们对细节的关注和客户服务无与伦比。我强烈推荐他们的服务。',
        rating: 4,
        image: '/images/testimonials/michael.jpg',
      },
    ],
    projects: {
      title: 'Recent Projects',
      title_zh: '近期项目',
      subtitle: 'Our Work Speaks For Itself',
      subtitle_zh: '我们的工程案例',
      items: [
        {
          title: 'Commercial Development',
          title_zh: '商业开发',
          description: 'Commercial site preparation and excavation',
          description_zh: '商业地点准备和挖掘',
          image: '/images/projects/commercial.jpg',
          path: '/#projects',
        },
        {
          title: 'Residential Subdivision',
          title_zh: '住宅小区',
          description: 'Land clearing and grading for new housing development',
          description_zh: '为新住宅开发进行土地清理和平整',
          image: '/images/projects/residential.jpg',
          path: '/#projects',
        },
        {
          title: 'Highway Expansion',
          title_zh: '高速公路扩建',
          description: 'Major excavation work for highway widening project',
          description_zh: '高速公路拓宽项目的主要挖掘工作',
          image: '/images/projects/highway.jpg',
          path: '/#projects',
        },
      ],
      cta: {
        text: 'View All Projects',
        text_zh: '查看所有项目',
        path: '/#projects',
      }
    },
    contact: {
      title: 'Get In Touch',
      title_zh: '联系我们',
      subtitle: 'Ready to start your project?',
      subtitle_zh: '准备开始您的项目了吗？',
      description: 'Contact us today for a free consultation and quote.',
      description_zh: '立即联系我们获取免费咨询和报价。',
      cta: {
        text: 'Contact Us',
        text_zh: '联系我们',
        path: '/#contact',
      }
    }
  },
  footer: {
    columns: [
      {
        title: 'About Us',
        title_zh: '关于我们',
        content: 'Dozer provides professional services with a commitment to quality, safety, and customer satisfaction.',
        content_zh: '道择提供专业服务，致力于质量、安全和客户满意度。',
      },
      {
        title: 'Quick Links',
        title_zh: '快速链接',
        links: [
          { text: 'Home', text_zh: '首页', path: '/' },
          { text: 'About', text_zh: '关于我们', path: '/#about' },
          { text: 'Services', text_zh: '服务项目', path: '/#services' },
          { text: 'Projects', text_zh: '工程案例', path: '/#projects' },
          { text: 'Blog', text_zh: '博客资讯', path: '/#blog' },
          { text: 'Contact', text_zh: '联系我们', path: '/#contact' },
        ]
      },
      {
        title: 'Services',
        title_zh: '服务项目',
        links: [
          { text: 'Excavation', text_zh: '挖掘服务', path: '/#services' },
          { text: 'Grading', text_zh: '平整服务', path: '/#services' },
          { text: 'Demolition', text_zh: '拆除服务', path: '/#services' },
          { text: 'Land Clearing', text_zh: '土地清理', path: '/#services' },
        ]
      },
      {
        title: 'Contact',
        title_zh: '联系我们',
        contact: true
      }
    ]
  }
};

// Create context
const ContentContext = createContext();

// Set nested value helper function
const setNestedValue = (obj, pathArr, value) => {
  // ... existing code ...
};

// Content provider component
const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(defaultContent);
  const [loading, setLoading] = useState(false);

  // 从 API 加载内容
  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      try {
        
        const response = await fetch(`${API_BASE_URL}/api/content`);
        
       
        if (response.ok) {
          const responseText = await response.text();
          
          try {
            const data = JSON.parse(responseText);
            setContent(data);
            // 同步内容到 i18n
            syncI18nWithContent(data);
          } catch (parseError) {
            console.error('JSON解析错误:', parseError);
            console.error('收到的实际响应不是有效的JSON:', responseText.substring(0, 500));
            // 使用默认内容
            console.warn('由于JSON解析错误，使用默认内容');
            setContent(defaultContent);
            syncI18nWithContent(defaultContent);
          }
        } else {
          // 尝试读取错误响应内容
          const errorText = await response.text();
          console.error('API返回错误状态:', response.status, response.statusText);
          console.error('错误响应内容:', errorText);
          
          // 如果 API 返回错误，使用默认内容
          console.warn('无法从 API 获取内容，使用默认内容');
          setContent(defaultContent);
          syncI18nWithContent(defaultContent);
        }
      } catch (error) {
        console.error('获取内容时出错:', error);
        console.error('错误详情:', error.stack || '无堆栈信息');
        // 发生错误时使用默认内容
        setContent(defaultContent);
        syncI18nWithContent(defaultContent);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  // Update i18n resources based on content changes
  const syncI18nWithContent = (newContent) => {
    try {
      // 直接设置特定的翻译，避免动态解析复杂结构
      // 英文翻译
      const enTranslations = {
        navigation: {
          home: 'Home',
          about: 'About',
          services: 'Services',
          products: 'Products',
          projects: 'Projects',
          blog: 'Blog',
          contact: 'Contact',
          getQuote: newContent.header?.cta?.text || 'Get a Quote'
        },
        home: {
          hero: {
            title: newContent.home?.hero?.title_en || newContent.home?.hero?.title || 'Professional Dozer Services',
            subtitle: newContent.home?.hero?.subtitle_en || newContent.home?.hero?.subtitle || 'Experience reliability and excellence in every project',
            description: newContent.home?.hero?.description_en || newContent.home?.hero?.description || 'We provide cutting-edge solutions with the highest quality service to meet your needs.',
            cta: {
              primary: newContent.home?.hero?.cta?.primary?.text_en || newContent.home?.hero?.cta?.primary?.text || 'Our Products',
              secondary: newContent.home?.hero?.cta?.secondary?.text_en || newContent.home?.hero?.cta?.secondary?.text || 'Contact Us'
            }
          },
          about: {
            title: newContent.home?.about?.title_en || newContent.home?.about?.title || 'About Our Company',
            subtitle: newContent.home?.about?.subtitle_en || newContent.home?.about?.subtitle || 'Professional Excellence Since 1995',
            description: newContent.home?.about?.description_en || newContent.home?.about?.description || 'For over 25 years, we have been delivering exceptional service and innovative solutions to our clients.'
          },
          products: {
            title: newContent.home?.services?.title_en || newContent.home?.services?.title || 'Our Products',
            subtitle: newContent.home?.services?.subtitle_en || newContent.home?.services?.subtitle || 'What We Offer'
          }
        },
        footer: {
          copyright: newContent.footer?.copyright_en || newContent.footer?.copyright || '© 2023 Dozer. All rights reserved.'
        }
      };

      // 中文翻译
      const zhTranslations = {
        navigation: {
          home: '首页',
          about: '关于我们',
          services: '服务项目',
          projects: '工程案例',
          products: '产品',
          contact: '联系我们',
          getQuote: newContent.header?.cta?.text_zh || '获取报价'
        },
        home: {
          hero: {
            title: newContent.home?.hero?.title_zh || '专业的道择工程服务',
            subtitle: newContent.home?.hero?.subtitle_zh || '在每个项目中体验可靠性和卓越性',
            description: newContent.home?.hero?.description_zh || '我们提供前沿解决方案，以最高质量的服务满足您的需求。',
            cta: {
              primary: newContent.home?.hero?.cta?.primary?.text_zh || '我们的服务',
              secondary: newContent.home?.hero?.cta?.secondary?.text_zh || '联系我们'
            }
          },
          about: {
            title: newContent.home?.about?.title_zh || '关于我们',
            subtitle: newContent.home?.about?.subtitle_zh || '自1995年以来的专业服务',
            description: newContent.home?.about?.description_zh || '在过去的25年里，我们一直为客户提供卓越的服务和创新解决方案。'
          },
          products: {
            title: newContent.home?.services?.title_zh || '产品',
            subtitle: newContent.home?.services?.subtitle_zh || '我们的产品'
          }
        },
        footer: {
          copyright: newContent.footer?.copyright_zh || '© 2023 道择. 保留所有权利。'
        }
      };

      // 更新 i18n 资源
      console.log('更新 i18n 资源:', { enTranslations, zhTranslations });
      
      i18n.addResourceBundle('en', 'translation', enTranslations, true, true);
      i18n.addResourceBundle('zh', 'translation', zhTranslations, true, true);
    } catch (error) {
      console.error('同步翻译时出错:', error);
    }
  };

  // 更新内容并保存到 DynamoDB
  const updateContent = async (newContent) => {
    setLoading(true);
    try {
      // 更新本地状态
      setContent(newContent);
      
      // 同步到 i18n
      syncI18nWithContent(newContent);
      
      // 保存到 DynamoDB
      const response = await fetch(`${API_BASE_URL}/api/content`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newContent),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('保存内容时出错:', errorData);
        alert('保存内容失败，请查看控制台了解详情');
      } else {
        console.log('内容已成功保存到数据库');
      }
    } catch (error) {
      console.error('更新内容时出错:', error);
      alert('保存内容时发生错误，请查看控制台了解详情');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ContentContext.Provider value={{ content, loading, updateContent }}>
      {children}
    </ContentContext.Provider>
  );
};

// Custom hook for using content context
export const useContent = () => useContext(ContentContext);

export default ContentProvider; 